import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Enums, Tables } from "@/types";
import type { EditorDoc, EditorLine, EditorFee } from "@/modules/documents/DocumentEditor";

export type Quote = Tables<"quotes">;
export type QuoteItem = Tables<"quote_items">;
export type QuoteFee = Tables<"quote_fees">;
export type QuoteStatus = Enums<"quote_status">;

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "Brouillon", sent: "Envoyé", viewed: "Consulté", negotiating: "En négociation", accepted: "Accepté", rejected: "Refusé", expired: "Expiré",
};
export const QUOTE_STATUS_VARIANT: Record<QuoteStatus, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "outline", sent: "secondary", viewed: "secondary", negotiating: "secondary", accepted: "default", rejected: "destructive", expired: "outline",
};

export type QuoteWithRelations = Quote & { clients: { id: string; legal_name: string; billing_email: string | null } | null; quote_items: QuoteItem[]; quote_fees: QuoteFee[] };

export function useQuotes(opts: { status?: string; clientId?: string } = {}) {
  return useQuery({
    queryKey: ["quotes", opts],
    queryFn: async () => {
      let q = supabase.from("quotes").select("*, clients(id, legal_name, billing_email)").order("created_at", { ascending: false });
      if (opts.status && opts.status !== "all") q = q.eq("status", opts.status as QuoteStatus);
      if (opts.clientId) q = q.eq("client_id", opts.clientId);
      const { data, error } = await q; if (error) throw error; return data;
    },
  });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ["quote", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.from("quotes")
        .select("*, clients(id, legal_name, billing_email), quote_items(*), quote_fees(*)")
        .eq("id", id!).single();
      if (error) throw error;
      const q = data as unknown as QuoteWithRelations;
      q.quote_items.sort((a, b) => a.position - b.position);
      q.quote_fees.sort((a, b) => a.position - b.position);
      return q;
    },
  });
}

export function useQuoteVersions(rootId: string | null | undefined) {
  return useQuery({
    queryKey: ["quote_versions", rootId],
    enabled: Boolean(rootId),
    queryFn: async () => { const { data, error } = await supabase.from("quotes").select("id, version, status, number, total, created_at").eq("root_id", rootId!).order("version"); if (error) throw error; return data; },
  });
}

export function useQuoteEvents(quoteId: string | undefined) {
  return useQuery({
    queryKey: ["quote_events", quoteId],
    enabled: Boolean(quoteId),
    queryFn: async () => { const { data, error } = await supabase.from("quote_events").select("*, profiles!quote_events_actor_profile_fk(full_name, email)").eq("quote_id", quoteId!).order("created_at", { ascending: false }); if (error) throw error; return data; },
  });
}

/** Convertit un devis chargé en document d'édition. */
export function quoteToDoc(q: QuoteWithRelations): EditorDoc {
  return {
    currency: q.currency,
    lines: q.quote_items.map<EditorLine>((it) => ({
      key: it.id, product_id: it.product_id, sku: it.sku, label: it.label, description: it.description ?? "",
      quantity: Number(it.quantity), unit_price: Number(it.unit_price), catalog_price: it.catalog_price === null ? null : Number(it.catalog_price),
      price_source: (it.price_source as EditorLine["price_source"]) ?? "manual", discount_type: it.discount_type, discount_value: it.discount_value === null ? null : Number(it.discount_value),
    })),
    fees: q.quote_fees.map<EditorFee>((f) => ({ key: f.id, fee_type_id: f.fee_type_id, label: f.label, amount: Number(f.amount) })),
    global_discount_type: q.global_discount_type, global_discount_value: q.global_discount_value === null ? null : Number(q.global_discount_value),
    tax_rate_pct: Number(q.tax_rate_pct), tax_label: q.tax_label, deposit_pct: Number(q.deposit_pct),
  };
}

export interface QuoteHeader {
  client_id: string; contact_id: string | null; title: string | null; issue_date: string; valid_until: string | null;
  payment_days: number; tax_regime_id: string | null; tax_label: string | null; tax_rate_pct: number; tax_mention: string | null;
  notes_client: string | null; notes_internal: string | null; terms: Record<string, unknown>;
}

/**
 * Enregistre un brouillon : en-tête, puis lignes et frais remplacés en bloc.
 * Les totaux sont recalculés par la base (triggers) — on relit ensuite.
 */
export function useSaveQuoteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; header: QuoteHeader; doc: EditorDoc }) => {
      const { header, doc } = input;
      const base = {
        ...header, currency: doc.currency, deposit_pct: doc.deposit_pct,
        global_discount_type: doc.global_discount_type, global_discount_value: doc.global_discount_value,
        tax_rate_pct: doc.tax_rate_pct, tax_label: doc.tax_label, terms: header.terms as never,
      };
      let id = input.id;
      if (id) {
        const { error } = await supabase.from("quotes").update(base).eq("id", id); if (error) throw error;
        const d1 = await supabase.from("quote_items").delete().eq("quote_id", id); if (d1.error) throw d1.error;
        const d2 = await supabase.from("quote_fees").delete().eq("quote_id", id); if (d2.error) throw d2.error;
      } else {
        const { data, error } = await supabase.from("quotes").insert(base).select("id").single(); if (error) throw error; id = data.id;
      }
      if (doc.lines.length) {
        const { error } = await supabase.from("quote_items").insert(doc.lines.map((l, i) => ({
          quote_id: id!, position: i + 1, product_id: l.product_id, sku: l.sku, label: l.label || "Ligne", description: l.description || null,
          quantity: l.quantity, unit_price: l.unit_price, catalog_price: l.catalog_price, price_source: l.price_source,
          discount_type: l.discount_type, discount_value: l.discount_type ? l.discount_value ?? 0 : null,
        })));
        if (error) throw error;
      }
      if (doc.fees.length) {
        const { error } = await supabase.from("quote_fees").insert(doc.fees.map((f, i) => ({ quote_id: id!, position: i + 1, fee_type_id: f.fee_type_id, label: f.label || "Frais", amount: f.amount })));
        if (error) throw error;
      }
      return id!;
    },
    onSuccess: (id) => { void qc.invalidateQueries({ queryKey: ["quotes"] }); void qc.invalidateQueries({ queryKey: ["quote", id] }); void qc.invalidateQueries({ queryKey: ["quote_versions"] }); logger.info("quote.saved", { quote_id: id }); },
    onError: (e) => { toast.error((e as { message?: string }).message?.includes("émis") ? "Ce devis est émis : crée une nouvelle version." : "Enregistrement impossible."); logger.error("quote.save", e); },
  });
}

export function useQuoteAction(quoteId: string) {
  const qc = useQueryClient();
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["quote", quoteId] }); void qc.invalidateQueries({ queryKey: ["quotes"] }); void qc.invalidateQueries({ queryKey: ["quote_versions"] }); void qc.invalidateQueries({ queryKey: ["quote_events", quoteId] }); void qc.invalidateQueries({ queryKey: ["orders"] }); };
  const setStatus = useMutation({
    mutationFn: async (input: { status: QuoteStatus; extra?: Partial<Quote> }) => {
      const { error } = await supabase.from("quotes").update({ status: input.status, ...(input.extra ?? {}) } as never).eq("id", quoteId);
      if (error) throw error;
    },
    onSuccess: refresh,
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Action impossible."); logger.error("quote.status", e); },
  });
  const newVersion = useMutation({
    mutationFn: async () => { const { data, error } = await supabase.rpc("quote_new_version", { _quote_id: quoteId }); if (error) throw error; return data as string; },
    onSuccess: refresh,
    onError: (e) => { toast.error("Impossible de créer la version."); logger.error("quote.version", e); },
  });
  const toOrder = useMutation({
    mutationFn: async () => { const { data, error } = await supabase.rpc("quote_to_order", { _quote_id: quoteId }); if (error) throw error; return data as string; },
    onSuccess: refresh,
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Conversion impossible."); logger.error("quote.to_order", e); },
  });
  const send = useMutation({
    mutationFn: async (input: { to: string; message?: string }) => {
      const { data, error } = await supabase.functions.invoke("send-quote", { body: { quote_id: quoteId, to: input.to, message: input.message } });
      if (error) throw error;
      return data as { ok: boolean };
    },
    onSuccess: () => { refresh(); toast.success("Devis envoyé"); },
    onError: (e) => { toast.error("Envoi impossible. Le devis reste en l'état."); logger.error("quote.send", e); },
  });
  return { setStatus, newVersion, toOrder, send };
}
